import * as React from 'react'

import { LocationType } from '~/client/graph/types.generated'
import {
  formatLocationSubtitle,
  HIERARCHY_SEPARATOR,
} from '~/client/src/shared/components/HierarchyChains'
import LocationIcon from '~/client/src/shared/components/LocationIcon/LocationIcon'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import type LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'

import {
  IObjectReferenceProps,
  ObjectReference,
  ObjectReferenceSize,
} from './ObjectReference'
import { StackedReference } from './StackedReference'

const ICON_PIXEL_SIZE: Record<ObjectReferenceSize, number> = {
  md: 20,
  sm: 18,
  // Project icon viewBox is 16×19 (vs App* icons' 24×24) — 16 keeps its
  // visible footprint in line with sibling 20px icons.
  xs: 16,
  xxs: 14,
  dense: 16,
}

// Explicit size class — `Button`'s `[&_svg:not([class*=size-])]:size-4` rule
// would otherwise clamp this SVG to 16px regardless of the pixel size prop.
const ICON_SIZE_CLASS: Record<ObjectReferenceSize, string> = {
  md: 'tw:size-5',
  sm: 'tw:size-[18px]',
  xs: 'tw:size-4',
  xxs: 'tw:size-[14px]',
  dense: 'tw:size-4',
}

type ILocationReferenceProps = Omit<IObjectReferenceProps, 'icon'> & {
  /** MapViewLocationIcon enum value. */
  iconName: MapViewLocationIcon
} & (
    | {
        /**
         * Ancestor names, root first — what `getLocationParentNames` returns. Rendered as
         * a muted second line under the name. Omit for a location with no ancestors to
         * show: the reference then stays a single line.
         */
        parentNames?: undefined
        resourceCode?: undefined
      }
    | {
        parentNames: string[]
        /**
         * Leads the second line, before the parent path, as the resource picker shows it.
         * Required with the second line, so no stacked reference drops it; undefined when
         * the location has none.
         */
        resourceCode: string | undefined
        /**
         * Stacking rules the variant out of `link`. A clickable `link` renders a Button,
         * whose own horizontal padding insets the first row while the second row is
         * indented from the column's edge — the two lines would no longer align. Both
         * variants allowed here are plain spans, so the indent holds.
         */
        variant: 'plain' | 'propertyLink'
      }
  )

/**
 * A location's name beside its type glyph, tinted with the location's own colour, and —
 * when ancestors are supplied — the path down to it on a second, muted line.
 */
export const LocationReference: React.FC<ILocationReferenceProps> = ({
  iconName,
  size = 'md',
  parentNames,
  resourceCode,
  ...rest
}) => {
  const reference = (
    <ObjectReference
      icon={
        <LocationIcon
          iconName={iconName}
          size={ICON_PIXEL_SIZE[size]}
          className={ICON_SIZE_CLASS[size]}
        />
      }
      size={size}
      {...rest}
    />
  )

  return (
    <StackedReference
      reference={reference}
      size={size}
      secondLine={formatLocationSubtitle(
        resourceCode,
        parentNames?.filter(Boolean).join(HIERARCHY_SEPARATOR),
      )}
    />
  )
}

/**
 * The location's ancestors as `LocationReference` wants them: root first, nearest parent
 * last. `getHierarchyChains` walks upwards and so returns them leaf first.
 */
export function getLocationParentNames(
  location: LocationBase,
  tagStoreByTagTypeMap: any,
): string[] {
  return getDisplayHierarchyChains(location, tagStoreByTagTypeMap)
    .slice()
    .reverse()
}

/**
 * The ancestors to show for a location, leaf first. Equipment shows none: it is
 * identified by its ID, and its parent chain is a property rather than its identity.
 */
export function getDisplayHierarchyChains(
  location: LocationBase,
  tagStoreByTagTypeMap: any,
): string[] {
  return location?.type === LocationType.OffloadingEquipment
    ? []
    : location.getHierarchyChains(tagStoreByTagTypeMap)
}
